import { useState, useEffect, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Redirect } from "wouter";
import { User, Lock, Save, Loader2, ShieldCheck, ShieldOff, Copy, Download, Check, RefreshCw, KeyRound, Camera, Trash2, Upload, Bell, MapPin, Star } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { useAuth } from "@/lib/auth-context";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useActiveLocations } from "@/hooks/use-active-locations";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
} from "@/components/ui/input-otp";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getAvatarSrc } from "@/components/user-avatar";
import { getQueryFn } from "@/lib/queryClient";
import { PasswordRequirementsChecklist, PasswordStrengthIndicator } from "@/components/password-requirements";
import { validatePassword } from "@/lib/password-validation";
import QRCode from "qrcode";
import { usePageTitle } from "@/hooks/use-page-title";

const profileFormSchema = z.object({
  firstName: z.string().max(100, "First name must be 100 characters or less").nullable(),
  lastName: z.string().max(100, "Last name must be 100 characters or less").nullable(),
  username: z.string()
    .min(3, "Username must be at least 3 characters")
    .max(50, "Username must be 50 characters or less")
    .regex(/^[a-zA-Z0-9_]+$/, "Username can only contain letters, numbers, and underscores"),
});

type ProfileFormData = z.infer<typeof profileFormSchema>;

const changePasswordFormSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: z.string().min(1, "New password is required"),
  confirmPassword: z.string().min(1, "Please confirm your new password"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
}).refine((data) => validatePassword(data.newPassword).isValid, {
  message: "Password does not meet requirements",
  path: ["newPassword"],
});

type ChangePasswordFormData = z.infer<typeof changePasswordFormSchema>;

interface NotificationPrefs {
  behaviorAlerts: boolean;
  alertFlags: boolean;
  locationTransfers: boolean;
  outcomes: boolean;
  fileUploads: boolean;
  profileUpdates: boolean;
}

const NOTIF_PREF_LABELS: { key: keyof NotificationPrefs; label: string; description: string }[] = [
  { key: "behaviorAlerts", label: "Behavior Alerts", description: "Behavior alert emails are recorded" },
  { key: "alertFlags", label: "Alert Flags", description: "New alert flags added to animals" },
  { key: "locationTransfers", label: "Location Transfers", description: "Animals moved to a different location" },
  { key: "outcomes", label: "Outcomes", description: "Animals adopted, returned to owner, transferred, etc." },
  { key: "fileUploads", label: "File Uploads", description: "New files or attachments added to animals" },
  { key: "profileUpdates", label: "Adoption Readiness & Evaluation Progress", description: "Receive updates as animals progress through their adoption readiness and evaluation process." },
];

function NotificationPreferencesSection() {
  const { toast } = useToast();

  const { data: prefs, isLoading } = useQuery<NotificationPrefs>({
    queryKey: ["/api/users/me/notification-preferences"],
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<NotificationPrefs>) => {
      return apiRequest("PATCH", "/api/users/me/notification-preferences", updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/users/me/notification-preferences"] });
      toast({ title: "Preferences saved" });
    },
    onError: () => {
      toast({ title: "Failed to save preferences", variant: "destructive" });
    },
  });

  const handleToggle = (key: keyof NotificationPrefs, checked: boolean) => {
    updateMutation.mutate({ [key]: checked });
  };

  if (isLoading) {
    return (
      <Card data-testid="card-notification-preferences">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-6 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="card-notification-preferences">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Follow Notifications
        </CardTitle>
        <CardDescription>
          Choose which events trigger notifications for animals you follow
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {NOTIF_PREF_LABELS.map(({ key, label, description }) => (
          <div key={key} className="flex items-start gap-3">
            <Checkbox
              id={`notif-${key}`}
              checked={prefs?.[key] ?? true}
              onCheckedChange={(checked) => handleToggle(key, !!checked)}
              disabled={updateMutation.isPending}
              data-testid={`checkbox-notif-${key}`}
            />
            <label htmlFor={`notif-${key}`} className="flex flex-col gap-0.5 cursor-pointer leading-none">
              <span className="text-sm font-medium">{label}</span>
              <span className="text-xs text-muted-foreground">{description}</span>
            </label>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function LocationPreferencesSection() {
  const { toast } = useToast();
  const { locations: activeLocations, isLoading: locationsLoading } = useActiveLocations();

  const { data: userLocs, isLoading: userLocsLoading } = useQuery<{ locationCodes: string[]; defaultLocationCode: string | null }>({
    queryKey: ["/api/account/locations"],
  });

  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [defaultCode, setDefaultCode] = useState<string | null>(null);

  const updateMutation = useMutation({
    mutationFn: async ({ codes, newDefault }: { codes: string[]; newDefault: string | null }) => {
      return apiRequest("PUT", "/api/account/locations", {
        locationCodes: codes,
        defaultLocationCode: newDefault,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/account/locations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: "Locations saved" });
    },
    onError: () => {
      toast({ title: "Failed to save locations", variant: "destructive" });
    },
  });

  useEffect(() => {
    if (userLocs && !updateMutation.isPending) {
      setSelectedCodes(userLocs.locationCodes);
      setDefaultCode(userLocs.defaultLocationCode);
    }
  }, [userLocs, updateMutation.isPending]);

  const handleToggle = (code: string, checked: boolean) => {
    const next = checked
      ? [...selectedCodes, code]
      : selectedCodes.filter(c => c !== code);
    const newDefault = !checked && defaultCode === code ? null : defaultCode;
    setSelectedCodes(next);
    setDefaultCode(newDefault);
    updateMutation.mutate({ codes: next, newDefault });
  };

  const handleSetDefault = (code: string) => {
    setDefaultCode(code);
    updateMutation.mutate({ codes: selectedCodes, newDefault: code });
  };

  const isLoading = locationsLoading || userLocsLoading;

  if (isLoading) {
    return (
      <Card data-testid="card-location-preferences">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-6 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="card-location-preferences">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          My Locations
        </CardTitle>
        <CardDescription>
          Select the locations you work at. Star one to set your default location across the app.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {activeLocations.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="text-no-locations-settings">No locations configured yet.</p>
        ) : (
          activeLocations.map((loc) => {
            const isSelected = selectedCodes.includes(loc.code);
            const isDefault = defaultCode === loc.code;
            const showStar = isSelected && selectedCodes.length >= 2;
            return (
              <div key={loc.code} className="flex items-center gap-3">
                <Checkbox
                  id={`loc-${loc.code}`}
                  checked={isSelected}
                  onCheckedChange={(checked) => handleToggle(loc.code, !!checked)}
                  disabled={updateMutation.isPending}
                  data-testid={`checkbox-setting-location-${loc.code}`}
                />
                <label htmlFor={`loc-${loc.code}`} className="flex-1 flex flex-col gap-0.5 cursor-pointer leading-none">
                  <span className="text-sm font-medium">{loc.label}</span>
                  <span className="text-xs text-muted-foreground">{loc.code}</span>
                </label>
                <div className="w-6 flex justify-center">
                  {showStar && (
                    <button
                      type="button"
                      onClick={() => handleSetDefault(loc.code)}
                      disabled={updateMutation.isPending}
                      data-testid={`button-star-location-${loc.code}`}
                      aria-label={isDefault ? "Remove as default location" : "Set as default location"}
                      className="p-0.5 rounded text-muted-foreground hover:text-amber-500 transition-colors"
                    >
                      <Star className={`h-3.5 w-3.5 ${isDefault ? "text-amber-500 fill-amber-500" : ""}`} />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

type TotpEnrollmentStep = "idle" | "qr" | "verify" | "recovery" | "done";

function TotpEnrollmentSection() {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const [enrollStep, setEnrollStep] = useState<TotpEnrollmentStep>("idle");
  const [qrDataUrl, setQrDataUrl] = useState<string>("");
  const [manualKey, setManualKey] = useState<string>("");
  const [showManualKey, setShowManualKey] = useState(false);
  const [verifyCode, setVerifyCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [codesSaved, setCodesSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [showRegenDialog, setShowRegenDialog] = useState(false);
  const [regenPassword, setRegenPassword] = useState("");
  const [regenCodes, setRegenCodes] = useState<string[]>([]);
  const [regenCodesSaved, setRegenCodesSaved] = useState(false);
  const [regenCopied, setRegenCopied] = useState(false);

  useEffect(() => {
    return () => {
      setQrDataUrl("");
      setManualKey("");
      setVerifyCode("");
      setRecoveryCodes([]);
      setDisablePassword("");
      setRegenPassword("");
      setRegenCodes([]);
    };
  }, []);

  const setupMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/account/totp/setup", {});
    },
    onSuccess: async (data: { uri: string; manualKey: string }) => {
      setManualKey(data.manualKey);
      try {
        const dataUrl = await QRCode.toDataURL(data.uri, {
          width: 200,
          margin: 2,
          color: { dark: "#000000", light: "#ffffff" },
        });
        setQrDataUrl(dataUrl);
      } catch {
        setQrDataUrl("");
      }
      setEnrollStep("qr");
    },
    onError: (error: any) => {
      if (error.message?.includes("409")) {
        toast({
          title: "Already enabled",
          description: "Two-factor authentication is already enabled on your account.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Setup failed",
          description: error.message || "Could not start two-factor setup.",
          variant: "destructive",
        });
      }
    },
  });

  const verifyMutation = useMutation({
    mutationFn: async (code: string) => {
      return apiRequest("POST", "/api/account/totp/verify", { code });
    },
    onSuccess: async (data: { recoveryCodes: string[] }) => {
      setRecoveryCodes(data.recoveryCodes);
      setEnrollStep("recovery");
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      await refreshUser();
    },
    onError: (error: any) => {
      toast({
        title: "Verification failed",
        description: error.message || "That code didn't match. Please check your authenticator app and enter the current 6-digit code.",
        variant: "destructive",
      });
      setVerifyCode("");
    },
  });

  const disableMutation = useMutation({
    mutationFn: async (password: string) => {
      return apiRequest("DELETE", "/api/account/totp", { password });
    },
    onSuccess: async () => {
      setShowDisableDialog(false);
      setDisablePassword("");
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      await refreshUser();
      toast({
        title: "Two-factor authentication disabled",
        description: "Your account is no longer protected by two-factor authentication.",
      });
    },
    onError: (error: any) => {
      if (error.message?.includes("403")) {
        toast({
          title: "Incorrect password",
          description: "The password you entered is incorrect.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: error.message || "Failed to disable two-factor authentication.",
          variant: "destructive",
        });
      }
    },
  });

  const regenMutation = useMutation({
    mutationFn: async (password: string) => {
      return apiRequest("POST", "/api/account/totp/recovery-codes", { password });
    },
    onSuccess: (data: { recoveryCodes: string[] }) => {
      setRegenCodes(data.recoveryCodes);
      setRegenCodesSaved(false);
      setRegenCopied(false);
    },
    onError: (error: any) => {
      if (error.message?.includes("403")) {
        toast({
          title: "Incorrect password",
          description: "The password you entered is incorrect.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: error.message || "Failed to regenerate recovery codes.",
          variant: "destructive",
        });
      }
    },
  });

  const handleCopyCodes = useCallback(async (codes: string[], setCopiedFn: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      setCopiedFn(true);
      toast({ title: "Codes copied to clipboard" });
      setTimeout(() => setCopiedFn(false), 2000);
    } catch {
      toast({
        title: "Copy failed",
        description: "Could not copy to clipboard. Please copy the codes manually.",
        variant: "destructive",
      });
    }
  }, [toast]);

  const handleDownloadCodes = useCallback((codes: string[]) => {
    const date = new Date().toLocaleDateString();
    const content = `BETI Recovery Codes — Generated ${date}\nEach code can only be used once.\n\n${codes.join("\n")}\n`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "beti-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const handleFinishEnrollment = () => {
    setEnrollStep("idle");
    setQrDataUrl("");
    setManualKey("");
    setVerifyCode("");
    setRecoveryCodes([]);
    setCodesSaved(false);
    setCopied(false);
    setShowManualKey(false);
  };

  const isEnrolled = user?.totpEnabled ?? false;

  if (enrollStep === "qr") {
    return (
      <Card data-testid="card-totp-setup">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Set Up Two-Factor Authentication
          </CardTitle>
          <CardDescription>Step 1 of 4</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Two-factor authentication adds an extra layer of security to your account. After entering your password, you'll also enter a short code from your phone to verify it's really you.
            </p>
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">To get started, you'll need an authenticator app on your phone.</span> If you don't have one, download Google Authenticator, Microsoft Authenticator, or Authy (all free).
            </p>
            <p className="text-sm text-muted-foreground">
              Open your authenticator app, tap the + button (or "Add account"), and scan the QR code below.
            </p>
          </div>

          {qrDataUrl && (
            <div className="flex justify-center py-4">
              <div className="bg-white p-3 rounded-md">
                <img
                  src={qrDataUrl}
                  alt="Scan this QR code with your authenticator app"
                  className="w-[200px] h-[200px]"
                  data-testid="img-totp-qr"
                />
              </div>
            </div>
          )}

          <div className="text-center">
            <button
              type="button"
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
              onClick={() => setShowManualKey(!showManualKey)}
              data-testid="button-toggle-manual-key"
            >
              {showManualKey ? "Hide manual key" : "Can't scan the code? Enter code manually"}
            </button>
            {showManualKey && (
              <div className="mt-3 p-3 bg-muted rounded-md">
                <p className="text-xs text-muted-foreground mb-1">Type this key into your authenticator app:</p>
                <p className="font-mono text-sm select-all break-all" data-testid="text-manual-key">{manualKey}</p>
              </div>
            )}
          </div>

          <div className="flex gap-2 justify-end">
            <Button
              variant="ghost"
              onClick={handleFinishEnrollment}
              data-testid="button-cancel-setup"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setVerifyCode("");
                setEnrollStep("verify");
              }}
              data-testid="button-next-to-verify"
            >
              Next
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (enrollStep === "verify") {
    return (
      <Card data-testid="card-totp-verify">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Verify Your Authenticator
          </CardTitle>
          <CardDescription>Step 2 of 4</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Your authenticator app should now be showing a 6-digit code that changes every 30 seconds. Enter the current code below to confirm everything is working.
          </p>

          <div className="flex justify-center py-2">
            <InputOTP
              maxLength={6}
              value={verifyCode}
              onChange={(value) => setVerifyCode(value)}
              disabled={verifyMutation.isPending}
              data-testid="input-totp-verify"
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
              </InputOTPGroup>
              <InputOTPSeparator />
              <InputOTPGroup>
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            Code not working? The code changes every 30 seconds — wait for a fresh code and try again. Make sure the time on your phone is set to automatic.
          </p>

          <div className="flex gap-2 justify-end">
            <Button
              variant="ghost"
              onClick={() => setEnrollStep("qr")}
              disabled={verifyMutation.isPending}
              data-testid="button-back-to-qr"
            >
              Back
            </Button>
            <Button
              onClick={() => verifyMutation.mutate(verifyCode)}
              disabled={verifyMutation.isPending || verifyCode.length !== 6}
              data-testid="button-verify-code"
            >
              {verifyMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Verify"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (enrollStep === "recovery") {
    return (
      <Card data-testid="card-totp-recovery">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            Save Your Recovery Codes
          </CardTitle>
          <CardDescription>Step 3 of 4</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm font-medium">
              If you ever lose access to your phone or authenticator app, you can use one of these recovery codes to sign in instead. Each code can only be used once.
            </p>
            <p className="text-sm text-muted-foreground">
              Save these codes somewhere safe — write them down, or download the file below. You won't be able to see these codes again after you leave this page.
            </p>
          </div>

          <RecoveryCodeDisplay codes={recoveryCodes} />

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCopyCodes(recoveryCodes, setCopied)}
              data-testid="button-copy-codes"
            >
              {copied ? <Check className="mr-1 h-3 w-3" /> : <Copy className="mr-1 h-3 w-3" />}
              {copied ? "Copied" : "Copy all codes"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDownloadCodes(recoveryCodes)}
              data-testid="button-download-codes"
            >
              <Download className="mr-1 h-3 w-3" />
              Download codes
            </Button>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Checkbox
              id="codes-saved"
              checked={codesSaved}
              onCheckedChange={(checked) => setCodesSaved(checked === true)}
              data-testid="checkbox-codes-saved"
            />
            <label htmlFor="codes-saved" className="text-sm cursor-pointer select-none">
              I have saved my recovery codes in a safe place
            </label>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={() => setEnrollStep("done")}
              disabled={!codesSaved}
              data-testid="button-complete-setup"
            >
              Complete Setup
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (enrollStep === "done") {
    return (
      <Card data-testid="card-totp-done">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Two-Factor Authentication Enabled
          </CardTitle>
          <CardDescription>Step 4 of 4</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Your account is now protected with two-factor authentication. The next time you sign in, you'll enter your password and then a code from your authenticator app.
          </p>
          <p className="text-sm text-muted-foreground">
            If you lose your phone, use one of the recovery codes you just saved. If you've lost both your phone and your recovery codes, contact a super admin — they can reset your two-factor authentication so you can set it up again.
          </p>
          <p className="text-sm text-muted-foreground">
            You can manage your two-factor settings anytime from your Account page.
          </p>
          <div className="flex justify-end">
            <Button onClick={handleFinishEnrollment} data-testid="button-done">
              Done
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="card-totp">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" />
          Two-Factor Authentication
        </CardTitle>
        <CardDescription>
          Add an extra layer of security to your account
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isEnrolled ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant="default" data-testid="badge-totp-status">
                <ShieldCheck className="mr-1 h-3 w-3" />
                Enabled
              </Badge>
              <span className="text-sm text-muted-foreground">
                Your account is protected with two-factor authentication.
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowRegenDialog(true);
                  setRegenPassword("");
                  setRegenCodes([]);
                  setRegenCodesSaved(false);
                  setRegenCopied(false);
                }}
                data-testid="button-regenerate-codes"
              >
                <RefreshCw className="mr-1 h-3 w-3" />
                Regenerate Recovery Codes
              </Button>
              {!user?.requireEnrollment && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowDisableDialog(true);
                    setDisablePassword("");
                  }}
                  data-testid="button-disable-totp"
                >
                  <ShieldOff className="mr-1 h-3 w-3" />
                  Disable
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Two-factor authentication adds an extra layer of security by requiring a code from your phone in addition to your password when you sign in.
            </p>
            <Button
              onClick={() => setupMutation.mutate()}
              disabled={setupMutation.isPending}
              data-testid="button-enable-totp"
            >
              {setupMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting up...
                </>
              ) : (
                <>
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  Enable Two-Factor Authentication
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>

      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent data-testid="dialog-disable-totp">
          <DialogHeader>
            <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              This will remove two-factor authentication from your account. You will only need your password to sign in. Enter your password to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Input
              type="password"
              placeholder="Enter your password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              disabled={disableMutation.isPending}
              data-testid="input-disable-password"
              autoComplete="current-password"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowDisableDialog(false)}
              disabled={disableMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => disableMutation.mutate(disablePassword)}
              disabled={disableMutation.isPending || !disablePassword}
              data-testid="button-confirm-disable"
            >
              {disableMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Disabling...
                </>
              ) : (
                "Disable"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showRegenDialog} onOpenChange={(open) => {
        if (!open) {
          setShowRegenDialog(false);
          setRegenCodes([]);
          setRegenPassword("");
        }
      }}>
        <DialogContent className="max-w-md" data-testid="dialog-regenerate-codes">
          <DialogHeader>
            <DialogTitle>
              {regenCodes.length > 0 ? "New Recovery Codes" : "Regenerate Recovery Codes"}
            </DialogTitle>
            <DialogDescription>
              {regenCodes.length > 0
                ? "Your old recovery codes have been replaced. Save these new codes — you won't be able to see them again."
                : "This will replace your existing recovery codes. Your old codes will stop working. Enter your password to continue."}
            </DialogDescription>
          </DialogHeader>

          {regenCodes.length === 0 ? (
            <>
              <div className="py-2">
                <Input
                  type="password"
                  placeholder="Enter your password"
                  value={regenPassword}
                  onChange={(e) => setRegenPassword(e.target.value)}
                  disabled={regenMutation.isPending}
                  data-testid="input-regen-password"
                  autoComplete="current-password"
                />
              </div>
              <DialogFooter className="gap-2">
                <Button
                  variant="ghost"
                  onClick={() => setShowRegenDialog(false)}
                  disabled={regenMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => regenMutation.mutate(regenPassword)}
                  disabled={regenMutation.isPending || !regenPassword}
                  data-testid="button-confirm-regenerate"
                >
                  {regenMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    "Regenerate"
                  )}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <RecoveryCodeDisplay codes={regenCodes} />
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopyCodes(regenCodes, setRegenCopied)}
                  data-testid="button-copy-regen-codes"
                >
                  {regenCopied ? <Check className="mr-1 h-3 w-3" /> : <Copy className="mr-1 h-3 w-3" />}
                  {regenCopied ? "Copied" : "Copy all codes"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownloadCodes(regenCodes)}
                  data-testid="button-download-regen-codes"
                >
                  <Download className="mr-1 h-3 w-3" />
                  Download codes
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="regen-codes-saved"
                  checked={regenCodesSaved}
                  onCheckedChange={(checked) => setRegenCodesSaved(checked === true)}
                  data-testid="checkbox-regen-codes-saved"
                />
                <label htmlFor="regen-codes-saved" className="text-sm cursor-pointer select-none">
                  I have saved my recovery codes in a safe place
                </label>
              </div>
              <DialogFooter>
                <Button
                  onClick={() => {
                    setShowRegenDialog(false);
                    setRegenCodes([]);
                    setRegenPassword("");
                  }}
                  disabled={!regenCodesSaved}
                  data-testid="button-done-regen"
                >
                  Done
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function RecoveryCodeDisplay({ codes }: { codes: string[] }) {
  return (
    <div className="grid grid-cols-2 gap-2 p-4 bg-muted rounded-md border" data-testid="recovery-codes-display">
      {codes.map((code, i) => (
        <span key={i} className="font-mono text-sm text-center" data-testid={`text-recovery-code-${i}`}>
          {code}
        </span>
      ))}
    </div>
  );
}

interface DefaultAvatar {
  key: string;
  label: string;
  url: string;
}

function AvatarSection() {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const defaultsQuery = useQuery<{ defaults: DefaultAvatar[] }>({
    queryKey: ["/api/account/avatar/defaults"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const setDefaultMutation = useMutation({
    mutationFn: async (key: string) => {
      return apiRequest("PATCH", "/api/account/avatar", { type: "default", key });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      await refreshUser();
      setPickerOpen(false);
      toast({ title: "Avatar updated", description: "Your profile photo has been changed." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update avatar", variant: "destructive" });
    },
  });

  const removeMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("PATCH", "/api/account/avatar", { type: "remove" });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      await refreshUser();
      setPickerOpen(false);
      toast({ title: "Avatar removed", description: "Your profile photo has been removed." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to remove avatar", variant: "destructive" });
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      const csrfToken = document.cookie
        .split("; ")
        .find((c) => c.startsWith("XSRF-TOKEN="))
        ?.split("=")[1];
      const response = await fetch("/api/account/avatar/upload", {
        method: "POST",
        body: formData,
        credentials: "include",
        headers: csrfToken ? { "X-XSRF-TOKEN": csrfToken } : {},
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: "Upload failed" }));
        throw new Error(err.message);
      }
      return response.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      await refreshUser();
      setPickerOpen(false);
      setUploadPreview(null);
      toast({ title: "Avatar uploaded", description: "Your profile photo has been updated." });
    },
    onError: (error: Error) => {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    },
  });

  const compressImage = (file: File, maxDimension = 1024, quality = 0.85): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDimension || height > maxDimension) {
          const ratio = Math.min(maxDimension / width, maxDimension / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) { resolve(file); return; }
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) { resolve(file); return; }
            resolve(new File([blob], file.name, { type: "image/jpeg" }));
          },
          "image/jpeg",
          quality
        );
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast({ title: "Invalid file", description: "Please select a JPEG, PNG, or WebP image.", variant: "destructive" });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "File too large", description: "Maximum file size is 10MB.", variant: "destructive" });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setUploadPreview(reader.result as string);
    reader.readAsDataURL(file);

    try {
      const compressed = await compressImage(file);
      uploadMutation.mutate(compressed);
    } catch {
      uploadMutation.mutate(file);
    }
  };

  if (!user) return null;

  const displayName = [user.firstName, user.lastName].filter(Boolean).join(" ") || user.username;
  const avatarSrc = getAvatarSrc(user.avatarUrl, user.id);
  const initials = displayName.split(/\s+/).map(p => p[0]).join("").toUpperCase().slice(0, 2) || "?";
  const isPending = setDefaultMutation.isPending || removeMutation.isPending || uploadMutation.isPending;

  return (
    <>
      <Card data-testid="card-avatar">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Profile Photo
          </CardTitle>
          <CardDescription>
            Choose a profile photo or upload your own
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              {(uploadPreview || avatarSrc) && (
                <AvatarImage src={uploadPreview || avatarSrc} alt={displayName} />
              )}
              <AvatarFallback className="text-lg">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                onClick={() => setPickerOpen(true)}
                data-testid="button-change-avatar"
              >
                <Camera className="h-4 w-4 mr-2" />
                Change Photo
              </Button>
              {user.avatarUrl && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeMutation.mutate()}
                  disabled={isPending}
                  data-testid="button-remove-avatar"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-md" data-testid="dialog-avatar-picker">
          <DialogHeader>
            <DialogTitle>Choose Profile Photo</DialogTitle>
            <DialogDescription>
              Select a default avatar or upload your own image
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium mb-3">Default Avatars</p>
              <div className="grid grid-cols-4 gap-3 max-h-[320px] overflow-y-auto">
                {defaultsQuery.data?.defaults.map((avatar) => {
                  const isSelected = user.avatarUrl === `default:${avatar.key}`;
                  return (
                    <button
                      key={avatar.key}
                      onClick={() => setDefaultMutation.mutate(avatar.key)}
                      disabled={isPending}
                      className={cn(
                        "flex flex-col items-center gap-1 p-2 rounded-md transition-colors",
                        isSelected
                          ? "ring-2 ring-primary bg-primary/5"
                          : "hover-elevate"
                      )}
                      data-testid={`button-default-avatar-${avatar.key}`}
                    >
                      <Avatar className="h-14 w-14">
                        <AvatarImage src={avatar.url} alt={avatar.label} />
                        <AvatarFallback>{avatar.label[0]}</AvatarFallback>
                      </Avatar>
                      <span className="text-xs text-muted-foreground">{avatar.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Upload Custom Photo</p>
              <p className="text-xs text-muted-foreground mb-3">JPEG, PNG, or WebP. Max 10MB. Will be resized to 256x256.</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="input-avatar-upload"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={isPending}
                data-testid="button-upload-avatar"
              >
                {uploadMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Choose File
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function AccountPage() {
  usePageTitle("Settings");
  const { user, isLoading, refreshUser } = useAuth();
  const { toast } = useToast();
  const [isPasswordSheetOpen, setIsPasswordSheetOpen] = useState(false);

  const profileForm = useForm<ProfileFormData>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      username: user?.username ?? "",
    },
  });

  useEffect(() => {
    if (user) {
      profileForm.reset({
        firstName: user.firstName ?? "",
        lastName: user.lastName ?? "",
        username: user.username ?? "",
      });
    }
  }, [user, profileForm]);

  useEffect(() => {
    if (window.location.hash === "#two-factor") {
      document.getElementById("two-factor")?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const passwordForm = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordFormSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (data: ProfileFormData) => {
      return apiRequest("PATCH", "/api/account/profile", {
        firstName: data.firstName || null,
        lastName: data.lastName || null,
        username: data.username,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      await refreshUser();
      toast({
        title: "Profile updated",
        description: "Your profile has been saved successfully.",
      });
    },
    onError: (error: Error) => {
      const errorMessage = error.message || "";
      if (errorMessage.includes("409") && errorMessage.includes("USERNAME_TAKEN")) {
        profileForm.setError("username", { message: "That username is already taken. Please choose another." });
      } else {
        toast({
          title: "Error",
          description: "Failed to update profile",
          variant: "destructive",
        });
      }
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: async (data: ChangePasswordFormData) => {
      return apiRequest("POST", "/api/account/change-password", data);
    },
    onSuccess: () => {
      toast({
        title: "Password changed",
        description: "Your password has been changed successfully.",
      });
      setIsPasswordSheetOpen(false);
      passwordForm.reset();
    },
    onError: (error: Error) => {
      const errorMessage = error.message || "";
      if (errorMessage.includes("403") && errorMessage.includes("currentPassword")) {
        passwordForm.setError("currentPassword", { message: "Current password is incorrect" });
      } else if (errorMessage.includes("newPassword")) {
        passwordForm.setError("newPassword", { message: "Password does not meet requirements" });
      } else if (errorMessage.includes("confirmPassword")) {
        passwordForm.setError("confirmPassword", { message: "Passwords do not match" });
      } else {
        toast({
          title: "Error",
          description: "Failed to change password",
          variant: "destructive",
        });
      }
    },
  });

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 max-w-2xl space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-48" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) {
    return <Redirect to="/login" />;
  }

  const onProfileSubmit = (data: ProfileFormData) => {
    updateProfileMutation.mutate(data);
  };

  const onPasswordSubmit = (data: ChangePasswordFormData) => {
    changePasswordMutation.mutate(data);
  };

  return (
    <div className="flex-1 px-4 sm:px-6 pb-6 pt-4">
      <PageHeader
        breadcrumb={[{ label: "Account" }, { label: "Settings" }]}
        title={
          <span className="flex items-center gap-2" data-testid="heading-account">
            <User className="w-6 h-6 text-primary" />
            Settings
          </span>
        }
      />
      <p className="text-muted-foreground mb-6">
        Manage your profile, security settings, and preferences
      </p>

      <div className="space-y-10">

        <div>
          <h2 className="text-lg font-semibold mb-4">Account</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-6">
              <AvatarSection />
              <Card data-testid="card-profile">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Profile
                  </CardTitle>
                  <CardDescription>
                    Update your personal information
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Form {...profileForm}>
                    <form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="space-y-4">
                      <FormField
                        control={profileForm.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>First Name</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="Enter your first name" 
                                {...field} 
                                value={field.value ?? ""}
                                maxLength={100}
                                data-testid="input-first-name"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={profileForm.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Last Name</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="Enter your last name" 
                                {...field} 
                                value={field.value ?? ""}
                                maxLength={100}
                                data-testid="input-last-name"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={profileForm.control}
                        name="username"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Username</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="Enter your username" 
                                {...field}
                                maxLength={50}
                                data-testid="input-username"
                              />
                            </FormControl>
                            <FormDescription>
                              Letters, numbers, and underscores only
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <Input 
                          value={user.email} 
                          disabled 
                          className="bg-muted"
                          data-testid="input-email"
                        />
                        <FormDescription>
                          Email cannot be changed. Contact an administrator if needed.
                        </FormDescription>
                      </FormItem>

                      <Button 
                        type="submit" 
                        disabled={updateProfileMutation.isPending}
                        data-testid="button-save-profile"
                      >
                        {updateProfileMutation.isPending ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="mr-2 h-4 w-4" />
                            Save Changes
                          </>
                        )}
                      </Button>
                    </form>
                  </Form>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <div id="two-factor">
                <TotpEnrollmentSection />
              </div>
              <Card data-testid="card-security">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="h-5 w-5" />
                    Password
                  </CardTitle>
                  <CardDescription>
                    Manage your password
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    variant="outline" 
                    onClick={() => setIsPasswordSheetOpen(true)}
                    data-testid="button-change-password"
                  >
                    <Lock className="mr-2 h-4 w-4" />
                    Change Password
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-lg font-semibold mb-4">Preferences</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-6">
              <LocationPreferencesSection />
            </div>
            <div className="space-y-6">
              <NotificationPreferencesSection />
            </div>
          </div>
        </div>

      </div>

      <Sheet open={isPasswordSheetOpen} onOpenChange={setIsPasswordSheetOpen}>
        <SheetContent data-testid="sheet-change-password">
          <SheetHeader>
            <SheetTitle>Change Password</SheetTitle>
            <SheetDescription>
              Enter your current password and choose a new one
            </SheetDescription>
          </SheetHeader>
          <Form {...passwordForm}>
            <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-4 mt-6">
              <FormField
                control={passwordForm.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Current Password</FormLabel>
                    <FormControl>
                      <Input 
                        type="password" 
                        placeholder="Enter current password" 
                        autoComplete="current-password"
                        {...field}
                        data-testid="input-current-password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={passwordForm.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New Password</FormLabel>
                    <FormControl>
                      <Input 
                        type="password" 
                        placeholder="Enter new password" 
                        autoComplete="new-password"
                        {...field}
                        data-testid="input-new-password"
                      />
                    </FormControl>
                    <PasswordStrengthIndicator password={field.value} />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="bg-muted/50 rounded-lg p-3">
                <PasswordRequirementsChecklist password={passwordForm.watch("newPassword")} />
              </div>

              <FormField
                control={passwordForm.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm New Password</FormLabel>
                    <FormControl>
                      <Input 
                        type="password" 
                        placeholder="Confirm new password" 
                        autoComplete="new-password"
                        {...field}
                        data-testid="input-confirm-password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsPasswordSheetOpen(false);
                    passwordForm.reset();
                  }}
                  data-testid="button-cancel-password"
                >
                  Cancel
                </Button>
                {(() => {
                  const isSubmitDisabled = 
                    changePasswordMutation.isPending || 
                    !validatePassword(passwordForm.watch("newPassword")).isValid ||
                    passwordForm.watch("newPassword") !== passwordForm.watch("confirmPassword") ||
                    !passwordForm.watch("currentPassword");
                  return (
                    <Button 
                      type="submit" 
                      disabled={isSubmitDisabled}
                      aria-disabled={isSubmitDisabled}
                      data-testid="button-submit-password"
                    >
                      {changePasswordMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Changing...
                        </>
                      ) : (
                        "Change Password"
                      )}
                    </Button>
                  );
                })()}
              </div>
            </form>
          </Form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
